/**
 * Helpers for surfacing what the backend actually said.
 *
 * Several endpoints in this app return actionable diagnostics — FastAPI
 * `detail` strings on 400/404/422, and 200-with-warnings payloads carrying
 * `mapping_recommendations` / `warnings` that explain why a result is empty.
 * The UI was discarding both, replacing them with generic empty states and
 * "please try again", so a backend that had correctly diagnosed the problem
 * ("No completed schema found. Complete the...") told the user nothing.
 *
 * Use `extractErrorMessage` in catch blocks and error panels, and
 * `extractAdvisories` on successful responses that may still be degraded.
 */

/** Longest message we will render inline before it stops being readable. */
const MAX_MESSAGE_LENGTH = 600;

const truncate = (text: string): string =>
  text.length > MAX_MESSAGE_LENGTH ? `${text.slice(0, MAX_MESSAGE_LENGTH)}…` : text;

/**
 * FastAPI validation errors arrive as `detail: [{loc, msg, type}, ...]`.
 * Render them as "field: message" lines rather than "[object Object]".
 */
const formatValidationDetail = (detail: any[]): string =>
  detail
    .map((item) => {
      if (typeof item === 'string') return item;
      const loc = Array.isArray(item?.loc)
        ? item.loc.filter((p: any) => p !== 'body').join('.')
        : '';
      const msg = item?.msg ?? item?.message ?? JSON.stringify(item);
      return loc ? `${loc}: ${msg}` : String(msg);
    })
    .filter(Boolean)
    .join('\n');

/**
 * Pull the most specific human-readable message out of an axios error,
 * a fetch Response-like object, or a plain Error.
 */
export const extractErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string => {
  if (!error) return fallback;

  if (typeof error === 'string') return truncate(error);

  const err = error as any;
  const data = err?.response?.data;

  if (data) {
    if (typeof data === 'string' && data.trim()) return truncate(data);

    const detail = data.detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return truncate(formatValidationDetail(detail));
    }
    if (typeof detail === 'string' && detail.trim()) return truncate(detail);
    if (detail && typeof detail === 'object') {
      const nested = detail.message ?? detail.error ?? detail.msg;
      if (typeof nested === 'string' && nested.trim()) return truncate(nested);
    }

    for (const key of ['message', 'error', 'reason'] as const) {
      const value = data[key];
      if (typeof value === 'string' && value.trim()) return truncate(value);
    }
  }

  const status = err?.response?.status;
  if (typeof err?.message === 'string' && err.message.trim()) {
    // Axios' own "Request failed with status code 422" is less useful than the
    // status alone, so only prefer it when it says something more.
    if (!/^Request failed with status code \d+$/.test(err.message)) {
      return truncate(err.message);
    }
  }
  if (status) return `Request failed (HTTP ${status}).`;

  return fallback;
};

/**
 * Collect non-fatal advisories from a 2xx payload.
 *
 * Endpoints such as Step 6's data-flow generation answer 200 with an empty
 * result plus `mapping_recommendations` explaining why. Rendering only the
 * empty state hides the explanation, which is exactly the reported P0-3.
 */
export const extractAdvisories = (payload: any): string[] => {
  if (!payload || typeof payload !== 'object') return [];

  const fields = [
    'mapping_recommendations',
    'recommendations',
    'warnings',
    'errors',
    'messages',
    'notes',
  ] as const;

  const out: string[] = [];
  for (const field of fields) {
    const value = payload[field];
    if (typeof value === 'string' && value.trim()) {
      out.push(value.trim());
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) out.push(item.trim());
        else if (item && typeof item === 'object') {
          const msg = item.message ?? item.msg ?? item.detail;
          if (typeof msg === 'string' && msg.trim()) out.push(msg.trim());
        }
      }
    }
  }

  // De-duplicate: the same advisory often appears under two keys.
  return Array.from(new Set(out));
};
